import React, { FunctionComponent, useState, useEffect } from 'react';
import styled from 'styled-components';
import { DashboardWrapper, Card, Text, Button } from 'src/shared';
import { Colors } from 'src/utils/color';
import { Size } from 'src/shared/text';
import { OrchestrationService, TraceData } from 'src/services/orchestrationService';

const Container = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled(Text)`
  color: ${Colors.extra.black};
`;

const Controls = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const FilterButton = styled(Button)<{ active?: boolean }>`
  background-color: ${props => props.active ? Colors.theme.primary : Colors.extra.light_gray};
  color: ${props => props.active ? Colors.extra.white : Colors.extra.black};
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 12px;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.active ? Colors.theme.primary : Colors.extra.dark_gray};
    color: ${Colors.extra.white};
  }
`;

const TimelineContainer = styled.div`
  position: relative;
  padding-left: 32px;
`;

const TimelineLine = styled.div`
  position: absolute;
  left: 16px;
  top: 0;
  bottom: 0;
  width: 2px;
  background-color: ${Colors.theme.primary};
  opacity: 0.3;
`;

const TimelineItem = styled(Card)`
  position: relative;
  margin-bottom: 24px;
  padding: 20px;
  border-left: 4px solid ${Colors.theme.primary};
  
  &::before {
    content: '';
    position: absolute;
    left: -24px;
    top: 24px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: ${Colors.theme.primary};
    border: 3px solid ${Colors.extra.white};
    box-shadow: 0 0 0 2px ${Colors.theme.primary};
  }
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const ItemTitle = styled(Text)`
  font-weight: 600;
  color: ${Colors.extra.black};
`;

const ItemTimestamp = styled(Text)`
  font-size: 12px;
  color: ${Colors.extra.dark_gray};
`;

const ItemContent = styled.div`
  margin-bottom: 12px;
`;

const IntentBadge = styled.div`
  display: inline-block;
  background-color: ${Colors.theme.primary}20;
  color: ${Colors.theme.primary};
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const ToolsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
`;

const ToolBadge = styled.div<{ status: string }>`
  display: inline-block;
  background-color: ${props => {
    switch (props.status) {
      case 'success': return '#10b98120';
      case 'error': return '#ef444420';
      case 'pending': return '#f59e0b20';
      default: return Colors.extra.light_gray;
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'success': return '#10b981';
      case 'error': return '#ef4444';
      case 'pending': return '#f59e0b';
      default: return Colors.extra.black;
    }
  }};
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
`;

const StatusBadge = styled.div<{ status: string }>`
  display: inline-block;
  background-color: ${props => {
    switch (props.status) {
      case 'success': return '#10b981';
      case 'error': return '#ef4444';
      case 'pending': return '#f59e0b';
      default: return Colors.extra.dark_gray;
    }
  }};
  color: ${Colors.extra.white};
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
`;

const Duration = styled(Text)`
  font-size: 12px;
  color: ${Colors.extra.dark_gray};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: ${Colors.extra.dark_gray};
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: ${Colors.extra.dark_gray};
`;

const TimelineTraceView: FunctionComponent = () => {
  const [traceData, setTraceData] = useState<TraceData[]>([]);
  const [filteredData, setFilteredData] = useState<TraceData[]>([]);
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [useBackend, setUseBackend] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      
      try {
        if (useBackend) {
          // Load from backend
          const sessionHistory = await OrchestrationService.getSessionHistory();
          setTraceData(sessionHistory);
          setFilteredData(sessionHistory);
        } else {
          // Use mock data
          const mockData: TraceData[] = [
            {
              session_id: 'sess_001',
              timestamp: '2025-01-15T10:30:00Z',
              intent: 'analyze_retinal_scan',
              tools_used: ['image_analysis', 'risk_assessment'],
              status: 'success',
              duration: 2.3
            },
            {
              session_id: 'sess_002',
              timestamp: '2025-01-15T09:15:00Z',
              intent: 'patient_summary',
              tools_used: ['summarize_visit', 'extract_metrics'],
              status: 'success',
              duration: 1.8
            },
            {
              session_id: 'sess_003',
              timestamp: '2025-01-15T08:45:00Z',
              intent: 'diagnosis_recommendation',
              tools_used: ['clinical_analysis', 'treatment_planning'],
              status: 'pending',
              duration: 0
            }
          ];
          setTraceData(mockData);
          setFilteredData(mockData);
        }
      } catch (error) {
        console.error('Failed to load trace data:', error);
        // Fallback to mock data
        const mockData: TraceData[] = [
          {
            session_id: 'sess_001',
            timestamp: '2025-01-15T10:30:00Z',
            intent: 'analyze_retinal_scan',
            tools_used: ['image_analysis', 'risk_assessment'],
            status: 'success',
            duration: 2.3
          }
        ];
        setTraceData(mockData);
        setFilteredData(mockData);
      }
      
      setLoading(false);
    };

    loadData();
  }, [useBackend]);

  useEffect(() => {
    if (activeFilter === 'all') {
      setFilteredData(traceData);
    } else {
      setFilteredData(traceData.filter(item => item.status === activeFilter));
    }
  }, [activeFilter, traceData]);

  const handleRefresh = async () => {
    setLoading(true);
    
    try {
      if (useBackend) {
        const sessionHistory = await OrchestrationService.getSessionHistory();
        setTraceData(sessionHistory);
        setFilteredData(sessionHistory);
      } else {
        // Refresh mock data
        const mockData: TraceData[] = [
          {
            session_id: `sess_${Date.now()}`,
            timestamp: new Date().toISOString(),
            intent: 'new_analysis',
            tools_used: ['new_tool'],
            status: 'success',
            duration: 1.5
          },
          ...traceData
        ];
        setTraceData(mockData);
        setFilteredData(mockData);
      }
    } catch (error) {
      console.error('Failed to refresh trace data:', error);
    }
    
    setLoading(false);
  };

  const getStats = () => {
    const total = traceData.length;
    const success = traceData.filter(item => item.status === 'success').length;
    const error = traceData.filter(item => item.status === 'error').length;
    const pending = traceData.filter(item => item.status === 'pending').length;
    
    return { total, success, error, pending };
  };

  const stats = getStats();

  const formatIntent = (intent: string): string => {
    return intent.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatToolName = (tool: string): string => {
    return tool.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <DashboardWrapper>
        <Container>
          <LoadingState>
            <Text fontSize={Size.Medium}>Loading timeline data...</Text>
          </LoadingState>
        </Container>
      </DashboardWrapper>
    );
  }

  return (
    <DashboardWrapper>
      <Container>
        <Header>
          <div>
            <Title fontSize={Size.Large} fontWeight="600">
              Orchestration Timeline
            </Title>
            <Text fontSize={Size.Small} color={Colors.extra.dark_gray} marginTop={4}>
              View the flow of intent → tools → results over time
            </Text>
          </div>
          <Controls>
            <FilterButton
              active={activeFilter === 'all'}
              onClick={() => setActiveFilter('all')}
            >
              All ({stats.total})
            </FilterButton>
            <FilterButton
              active={activeFilter === 'success'}
              onClick={() => setActiveFilter('success')}
            >
              Success ({stats.success})
            </FilterButton>
            <FilterButton
              active={activeFilter === 'error'}
              onClick={() => setActiveFilter('error')}
            >
              Error ({stats.error})
            </FilterButton>
            <FilterButton
              active={activeFilter === 'pending'}
              onClick={() => setActiveFilter('pending')}
            >
              Pending ({stats.pending})
            </FilterButton>
            <FilterButton
              active={useBackend}
              onClick={() => setUseBackend(!useBackend)}
            >
              {useBackend ? 'Backend Data' : 'Mock Data'}
            </FilterButton>
            <Button onClick={handleRefresh}>
              Refresh
            </Button>
          </Controls>
        </Header>

        <TimelineContainer>
          <TimelineLine />
          {filteredData.length === 0 ? (
            <EmptyState>
              <Text fontSize={Size.Medium} marginBottom={8}>
                No timeline data found
              </Text>
              <Text fontSize={Size.Small} color={Colors.extra.dark_gray}>
                {activeFilter === 'all' 
                  ? 'No orchestration events available' 
                  : `No ${activeFilter} events found`}
              </Text>
            </EmptyState>
          ) : (
            filteredData.map((item, index) => (
              <TimelineItem key={item.session_id}>
                <ItemHeader>
                  <ItemTitle fontSize={Size.Medium}>
                    Session {item.session_id}
                  </ItemTitle>
                  <ItemTimestamp>
                    {new Date(item.timestamp).toLocaleString()}
                  </ItemTimestamp>
                </ItemHeader>
                
                <ItemContent>
                  <IntentBadge>
                    Intent: {formatIntent(item.intent)}
                  </IntentBadge>
                  
                  <ToolsList>
                    {item.tools_used.map((tool, toolIndex) => (
                      <ToolBadge key={toolIndex} status={item.status}>
                        {formatToolName(tool)}
                      </ToolBadge>
                    ))}
                  </ToolsList>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <StatusBadge status={item.status}>
                      {item.status.toUpperCase()}
                    </StatusBadge>
                    <Duration>
                      Duration: {item.duration}s
                    </Duration>
                  </div>
                </ItemContent>
              </TimelineItem>
            ))
          )}
        </TimelineContainer>
      </Container>
    </DashboardWrapper>
  );
};

export default TimelineTraceView;






